import { useEffect, useMemo } from 'react';
import { getMetaProfile } from './metaProfiles.js';

const META_BLUEPRINT = [
  { attr: 'name', key: 'description', field: 'description' },
  { attr: 'property', key: 'og:title', field: 'title' },
  { attr: 'property', key: 'og:description', field: 'description' },
  { attr: 'property', key: 'og:type', field: 'ogType' },
  { attr: 'property', key: 'og:url', field: 'url' },
  { attr: 'property', key: 'og:image', field: 'image' },
  { attr: 'property', key: 'og:image:alt', field: 'imageAlt' },
  { attr: 'property', key: 'og:site_name', field: 'siteName' },
  { attr: 'name', key: 'twitter:card', field: 'twitterCard' },
  { attr: 'name', key: 'twitter:title', field: 'title' },
  { attr: 'name', key: 'twitter:description', field: 'description' },
  { attr: 'name', key: 'twitter:image', field: 'image' },
  { attr: 'name', key: 'twitter:image:alt', field: 'imageAlt' },
];

function upsertMetaTag(attr, key, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

function setCanonical(href) {
  if (!href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let script = document.head.querySelector(`script[data-ld-id="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-ld-id', id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

/**
 * Applies the metadata for the given profile. Keeps the logic in one place so
 * route components stay focused on UI concerns. Optional `overrides` lets
 * pages customize title/description/url dynamically (e.g., per-invite).
 */
export default function MetaTags({ profileKey, overrides }) {
  const profile = useMemo(() => {
    const base = profileKey ? getMetaProfile(profileKey) : null;
    if (!base && !overrides) return null;
    if (!overrides) return base;
    return { ...(base || {}), ...overrides };
  }, [profileKey, overrides]);

  useEffect(() => {
    if (!profile) return;
    document.title = profile.title;
    META_BLUEPRINT.forEach(({ attr, key, field }) => {
      upsertMetaTag(attr, key, profile[field]);
    });
    if (profile.keywords) {
      const value = Array.isArray(profile.keywords)
        ? profile.keywords.join(', ')
        : profile.keywords;
      upsertMetaTag('name', 'keywords', value);
    }
    if (profile.robots) {
      upsertMetaTag('name', 'robots', profile.robots);
    }
    setCanonical(profile.url);

    if (profile.schema && typeof profile.schema === 'object') {
      Object.entries(profile.schema).forEach(([id, data]) => {
        upsertJsonLd(id, data);
      });
    }
  }, [profile]);

  return null;
}
